import { API_BASE_URL } from '../config/api'
import { getAuthHeaders, getAuthHeadersAsync } from '../utils/authUtils'

// Using proxy instead of direct API calls

export interface UserProfile {
  id: string
  email: string
  firstName: string
  lastName: string
  phone: string
  userType: string
  profileImage?: string
  isVerified: boolean
  emailVerified?: boolean
  phoneVerified?: boolean
  onboardingCompleted?: boolean
  organizationName?: string
  street?: string
  city?: string
  state?: string
  zipCode?: string
  volunteerIdVerification?: {
    id: string
    status: 'PENDING' | 'APPROVED' | 'REJECTED'
    submittedAt: string
    reviewedAt?: string | null
    rejectionReason?: string | null
  } | null
  /** Volunteer home coordinates for nearby delivery filtering */
  homeLatitude?: number | null
  homeLongitude?: number | null
  createdAt: string
  xp?: {
    level: number
    xp: number
    totalXp: number
  }
}

export interface Transaction {
  id: string
  userId: string
  type: string
  title: string
  description: string
  amount?: number
  status: string
  relatedId?: string
  createdAt: string
  updatedAt: string
}

export interface Badge {
  id: string
  name: string
  description: string
  icon: string
  category: string
  xpReward: number
  isActive: boolean
  createdAt: string
}

export interface UserBadge {
  id: string
  userId: string
  badgeId: string
  earnedAt: string
  isActive: boolean
  badge: Badge
}

export interface UserXP {
  id: string
  userId: string
  level: number
  xp: number
  totalXp: number
}

// Get user profile. Network/CORS failures throw so callers do not retry into Firebase quota.
export async function getProfile(forceRefresh: boolean = false): Promise<UserProfile | null> {
  const fetchOnce = async (refreshToken: boolean) => {
    const authHeaders = await getAuthHeadersAsync(refreshToken)
    if (!authHeaders.Authorization) {
      return { response: null as Response | null, missingToken: true }
    }
    const response = await fetch(`${API_BASE_URL}/api/profile`, {
      headers: authHeaders,
    })
    return { response, missingToken: false }
  }

  let { response, missingToken } = await fetchOnce(forceRefresh)
  if (missingToken) {
    console.warn('⚠️ No authentication token available for profile fetch')
    return null
  }
  if (!response) return null

  if (response.status === 401 && !forceRefresh) {
    const retry = await fetchOnce(true)
    if (retry.missingToken || !retry.response) return null
    response = retry.response
  }

  if (!response.ok) {
    return null
  }

  const data = await response.json()
  return data.data ?? null
}

// Update user profile
// Mark onboarding as completed
export async function completeOnboarding(): Promise<UserProfile | null> {
  try {
    const token = localStorage.getItem('token') || localStorage.getItem('firebaseToken')
    
    if (!token) {
      return null
    }
    
    const response = await fetch(`${API_BASE_URL}/api/profile`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        onboardingCompleted: true
      })
    })
    
    if (!response.ok) {
      throw new Error('Failed to complete onboarding')
    }
    
    const data = await response.json()
    return data.data
  } catch (error) {
    console.error('Error completing onboarding:', error)
    return null
  }
}

export async function updateProfile(profileData: Partial<UserProfile>): Promise<UserProfile | null> {
  try {
    const token = localStorage.getItem('token') || localStorage.getItem('firebaseToken')
    
    const response = await fetch(`${API_BASE_URL}/api/profile`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(profileData)
    })
    
    let data;
    try {
      data = await response.json();
    } catch (error) {
      throw new Error(`Server error: ${response.status} ${response.statusText}`);
    }
    
    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Token is not valid')
      }
      throw new Error(data.error || 'Failed to update profile')
    }
    
    return data.data
  } catch (error) {
    console.error('Error updating profile:', error)
    return null
  }
}

/** Upload directory photo for food banks / suppliers (shown on public listings). */
export async function uploadProfilePhoto(file: File): Promise<string> {
  const form = new FormData()
  form.append('photo', file)

  const authHeaders = await getAuthHeadersAsync(true)
  if (!authHeaders.Authorization) {
    throw new Error('No authentication token available. Please log in again.')
  }

  const response = await fetch(`${API_BASE_URL}/api/profile/photo`, {
    method: 'POST',
    headers: {
      // Do not set Content-Type for multipart; browser sets boundary
      Authorization: authHeaders.Authorization
    },
    body: form
  })

  const data = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw new Error(data.error || 'Failed to upload photo')
  }

  return data.data?.profileImage as string
}

// Get user transactions
export async function getTransactions(): Promise<Transaction[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/profile/transactions`, {
      headers: {
        ...getAuthHeaders()
      }
    })
    
    let data;
    try {
      data = await response.json();
    } catch (error) {
      throw new Error(`Server error: ${response.status} ${response.statusText}`);
    }
    
    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Token is not valid')
      }
      throw new Error(data.error || 'Failed to fetch transactions')
    }
    
    return data.data
  } catch (error) {
    console.error('Error fetching transactions:', error)
    return []
  }
}

// Get user badges
export async function getBadges(): Promise<UserBadge[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/profile/badges`, {
      headers: {
        ...getAuthHeaders()
      }
    })
    
    let data;
    try {
      data = await response.json();
    } catch (error) {
      throw new Error(`Server error: ${response.status} ${response.statusText}`);
    }
    
    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Token is not valid')
      }
      throw new Error(data.error || 'Failed to fetch badges')
    }
    
    return data.data
  } catch (error) {
    console.error('Error fetching badges:', error)
    return []
  }
}

// Get user XP
export async function getXP(): Promise<UserXP | null> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/profile/xp`, {
      headers: {
        ...getAuthHeaders()
      }
    })
    
    let data;
    try {
      data = await response.json();
    } catch (error) {
      throw new Error(`Server error: ${response.status} ${response.statusText}`);
    }
    
    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Token is not valid')
      }
      throw new Error(data.error || 'Failed to fetch XP')
    }
    
    return data.data
  } catch (error) {
    console.error('Error fetching XP:', error)
    return null
  }
}

// Change password
export async function changePassword(currentPassword: string, newPassword: string): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/profile/change-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify({
        currentPassword,
        newPassword
      })
    })
    
    let data;
    try {
      data = await response.json();
    } catch (error) {
      throw new Error(`Server error: ${response.status} ${response.statusText}`);
    }
    
    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Token is not valid')
      }
      throw new Error(data.error || 'Failed to change password')
    }
    
    return true
  } catch (error) {
    console.error('Error changing password:', error)
    return false
  }
} 